import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy, Download, KeyRound, Loader2, Plug, Plus, Trash2, Unplug } from "lucide-react";
import { useState } from "react";
import { useTRPC } from "../../../shared/api/trpc";
import { cn } from "../../../shared/lib/cn";
import { useI18n, useT } from "../../../shared/i18n/i18n";
import { formatRelativeTime } from "../../../shared/lib/format-relative-time";
import { mcpClients, pluginTargets, starterPromptKeys } from "./mcp-clients";
import { WebhooksSection } from "./webhooks-section";
import { Button, buttonVariants } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// What a token may do, said the way a person decides it rather than the way it
// is stored. Read is always on: a token that cannot read cannot do anything
// useful, and making people tick it is a step with one sensible answer.
const optionalScopes = [
  { key: "workItems", scope: "write:work_items" },
  { key: "sprints", scope: "write:sprints" },
  { key: "workspace", scope: "write:workspace" },
];

// Сколько живёт токен. Бессрочного варианта здесь нет специально: такой секрет
// никто не ротирует. Квартал — умолчание: не мешает работе и делает ротацию
// обычным делом.
const expiryOptions = [30, 90, 180, 365];
const defaultExpiryDays = 90;

export function IntegrationsPanel() {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const tokensQuery = useQuery(trpc.apiTokens.list.queryOptions());
  const revokeMutation = useMutation(trpc.apiTokens.revoke.mutationOptions());
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  // The secret lives here for as long as the page is open and nowhere else —
  // not in storage, not in a query cache. Leaving the page is what ends it.
  const [issued, setIssued] = useState<{ name: string; secret: string } | null>(null);
  const [openTrail, setOpenTrail] = useState<string | null>(null);

  const tokens = (tokensQuery.data ?? []).filter((token) => !token.revokedAt);

  async function revoke(tokenId: string) {
    await revokeMutation.mutateAsync({ tokenId });
    await queryClient.invalidateQueries({ queryKey: trpc.apiTokens.list.queryKey() });
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-8 px-6 py-6">
      <section className="flex flex-col gap-3">
        <header className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="text-sm font-medium">{t("integrations.tokens.title")}</h2>
            <p className="text-xs text-muted-foreground">
{t("integrations.tokens.description")}
            </p>
          </div>
          <Button onClick={() => setIsCreateOpen(true)} size="sm">
            <Plus data-icon="inline-start" />
            {t("integrations.tokens.new")}
          </Button>
        </header>

        {tokensQuery.isLoading ? (
          <p className="text-xs text-muted-foreground">{t("common.loading")}</p>
        ) : tokens.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t("integrations.tokens.empty")}</p>
        ) : (
          <div className="flex flex-col">
            {tokens.map((token) => (
              <div className="flex min-w-0 flex-col gap-1 border-b border-border/40 py-2 last:border-0" key={token.id}>
                <div className="flex items-center gap-3">
                <KeyRound className="size-4 shrink-0 text-muted-foreground" />
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-sm">{token.name}</span>
                    {token.ownerName ? (
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {t("integrations.tokens.actingAs", { name: token.ownerName })}
                      </span>
                    ) : null}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {token.tokenPrefix}··· · {token.scopes.join(", ")} ·{" "}
                    {token.lastUsedAt
                      ? t("integrations.tokens.lastUsed", { when: formatRelativeTime(token.lastUsedAt, t, locale) })
                      : t("integrations.tokens.never")}
                    {token.expiresAt ? ` · ${t("integrations.tokens.expires", { when: formatRelativeTime(token.expiresAt, t, locale) })}` : ""}
                  </span>
                  {token.expiresAt ? null : (
                    <span className="text-xs text-amber-600 dark:text-amber-400">{t("integrations.tokens.noExpiry")}</span>
                  )}
                </div>
                <Button
                  className="h-7 shrink-0 text-xs"
                  onClick={() => setOpenTrail(openTrail === token.id ? null : token.id)}
                  size="sm"
                  variant="ghost"
                >
                  {t("integrations.tokens.trail")}
                </Button>
                <Button
                  className="h-7 shrink-0 text-xs"
                  disabled={revokeMutation.isPending}
                  onClick={() => void revoke(token.id)}
                  size="sm"
                  variant="ghost"
                >
                  <Trash2 data-icon="inline-start" />
                  {t("integrations.tokens.revoke")}
                </Button>
                </div>
                {openTrail === token.id ? <TokenTrail tokenId={token.id} /> : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <PluginSection />

      <ConnectedAppsSection />

      <ConnectSection issued={issued} />

      <WebhooksSection copyable={CopyableCode} />

      <CreateTokenDialog
        onCreated={async (created) => {
          setIssued(created);
          setIsCreateOpen(false);
          await queryClient.invalidateQueries({ queryKey: trpc.apiTokens.list.queryKey() });
        }}
        onOpenChange={setIsCreateOpen}
        open={isCreateOpen}
      />
    </div>
  );
}

// The instance hands out its own plugin: the connection to this workspace plus
// the skills for working in it, installed in one step. Pointing a client at the
// MCP endpoint by hand still works and is right below — this is the version for
// people who would rather not read two config formats.
function PluginSection() {
  const t = useT();
  const [targetId, setTargetId] = useState(pluginTargets[0]?.id ?? "claude-code");
  const [isCopied, setIsCopied] = useState(false);
  const [areStepsOpen, setStepsOpen] = useState(false);
  const target = pluginTargets.find((candidate) => candidate.id === targetId) ?? pluginTargets[0];
  const origin = window.location.origin;
  if (!target) return null;
  const deepLink = target.deepLink?.({ origin }) ?? null;
  const alternative = target.alternative?.({ origin }) ?? null;

  async function copyCommand() {
    if (!target) return;
    try {
      await navigator.clipboard.writeText(target.command({ origin }));
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 1500);
    } catch {
      // Some browsers refuse the clipboard outright. Saying "Copied" when
      // nothing was copied is worse than showing the command to select by hand.
      setStepsOpen(true);
    }
  }

  return (
    <section className="flex min-w-0 flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-medium">{t("integrations.plugin.title")}</h2>
        <p className="text-xs text-muted-foreground">{t("integrations.plugin.description")}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {pluginTargets.map((candidate) => (
          <button
            className={cn(
              "rounded-md px-2.5 py-1 text-xs transition-colors",
              candidate.id === target.id ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-accent/50",
            )}
            key={candidate.id}
            onClick={() => {
              setTargetId(candidate.id);
              setIsCopied(false);
              setStepsOpen(false);
            }}
            type="button"
          >
            {candidate.name}
          </button>
        ))}
      </div>

      <div className="flex min-w-0 flex-col gap-2.5">
        <p className="text-xs text-muted-foreground">{t(`integrations.plugin.${target.id}Hint`, { origin })}</p>
        <div className="flex flex-wrap items-center gap-2">
          {deepLink ? (
            // A plain anchor, styled as a button: the Base UI button treats its
            // rendered element as a native button and swallows the navigation,
            // and this link's whole job is to be handed to the operating system.
            <a className={cn(buttonVariants({ size: "sm" }))} href={deepLink}>
              <Download data-icon="inline-start" />
              {t("integrations.plugin.install", { app: target.name })}
            </a>
          ) : null}
          <Button onClick={() => void copyCommand()} size="sm" variant={deepLink ? "outline" : "default"}>
            {isCopied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
            {isCopied ? t("integrations.plugin.copied") : t("integrations.plugin.copyCommand")}
          </Button>
        </div>
        <details className="min-w-0" onToggle={(event) => setStepsOpen(event.currentTarget.open)} open={areStepsOpen}>
          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
            {t("integrations.plugin.showSteps")}
          </summary>
          <div className="mt-2 flex min-w-0 flex-col gap-2">
            {target.steps({ origin }).map((step) => (
              <CopyableCode key={step} value={step} />
            ))}
            {alternative ? (
              <>
                <p className="mt-1 text-xs text-muted-foreground">{t(`integrations.plugin.${alternative.key}`)}</p>
                {alternative.commands.map((command) => (
                  <CopyableCode key={command} value={command} />
                ))}
              </>
            ) : null}
          </div>
        </details>
      </div>
    </section>
  );
}

// Apps that signed in as this person rather than carrying a pasted token.
// Nothing here is configured — the list exists so that "who did I let in" and
// "how do I stop them" have an answer in one place.
function ConnectedAppsSection() {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const appsQuery = useQuery(trpc.oauth.connectedApps.queryOptions());
  const disconnectMutation = useMutation(trpc.oauth.disconnect.mutationOptions());
  const apps = appsQuery.data ?? [];

  // Nothing connected and nothing ever connected is not a state worth a box on
  // the screen: the section appears the moment it has something to say.
  if (appsQuery.isPending || apps.length === 0) return null;

  async function disconnect(grantId: string) {
    await disconnectMutation.mutateAsync({ grantId });
    await queryClient.invalidateQueries({ queryKey: trpc.oauth.connectedApps.queryKey() });
  }

  return (
    <section className="flex flex-col gap-3">
      <header className="flex flex-col gap-0.5">
        <h2 className="text-sm font-medium">{t("connect.connectedApps")}</h2>
        <p className="text-xs text-muted-foreground">{t("connect.connectedAppsHint")}</p>
      </header>
      <div className="flex flex-col divide-y divide-border/60 rounded-lg border border-border/70">
        {apps.map((app) => (
          <div className="flex items-center gap-3 px-3 py-2.5" key={app.id}>
            <Plug className="size-4 shrink-0 text-muted-foreground" />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-xs font-medium">{app.clientName}</span>
              {/* Одно приложение подключают по нескольку раз, и строки выглядят
                  одинаково. Отключают всегда конкретное подключение, поэтому
                  здесь и дата, и права, и хвост идентификатора клиента. */}
              <span className="truncate text-[11px] text-muted-foreground">
                {t("connect.connectedSince", { when: formatRelativeTime(app.createdAt, t, locale) })}
                {" · "}
                {app.lastUsedAt
                  ? t("connect.lastUsed", { when: formatRelativeTime(app.lastUsedAt, t, locale) })
                  : t("connect.neverUsed")}
              </span>
              <span className="truncate font-mono text-[11px] text-muted-foreground/70">
                {app.scopes.join(", ")} · {t("connect.clientIdLabel", { id: app.clientId.slice(0, 8) })}
              </span>
            </div>
            <Button
              className="shrink-0"
              disabled={disconnectMutation.isPending}
              onClick={() => void disconnect(app.id)}
              size="sm"
              variant="ghost"
            >
              <Unplug data-icon="inline-start" />
              {t("connect.disconnect")}
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}

// A token is trusted with real access, so "what has it been doing" must be
// answerable without reading the database. The activity log already records
// which token acted; this is that trail, filtered.
function TokenTrail({ tokenId }: { tokenId: string }) {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const trailQuery = useQuery(trpc.activity.list.queryOptions({ actorTokenId: tokenId, limit: 10 }));
  const events = trailQuery.data ?? [];

  if (trailQuery.isLoading) return <p className="pl-1 text-xs text-muted-foreground">{t("common.loading")}</p>;
  if (events.length === 0) return <p className="pl-1 text-xs text-muted-foreground">{t("integrations.tokens.trailEmpty")}</p>;

  return (
    <div className="flex flex-col gap-1 rounded-md bg-muted/30 px-3 py-2">
      {events.map((event) => (
        <div className="flex items-center gap-2 text-xs" key={event.id}>
          <span className="min-w-0 flex-1 truncate text-muted-foreground">
            {t(`webhooks.event.${event.eventType}`)}
            {event.subjectName ? ` · ${event.subjectName}` : ""}
          </span>
          <span className="shrink-0 text-muted-foreground/70">{formatRelativeTime(event.createdAt, t, locale)}</span>
        </div>
      ))}
    </div>
  );
}

// Pick the client you actually use and the snippet below is already filled in
// with the address — and with the secret, when a token was just made. Sending
// someone to a docs page to assemble that themselves is where integrations die.
function ConnectSection({ issued }: { issued: { name: string; secret: string } | null }) {
  const t = useT();
  const { locale } = useI18n();
  const [clientId, setClientId] = useState(mcpClients[0]?.id ?? "claude-code");
  const client = mcpClients.find((candidate) => candidate.id === clientId) ?? mcpClients[0];
  const url = `${window.location.origin}/mcp`;
  // No token unless one was just made: the address alone is now the whole
  // configuration, and the client asks the person to sign in.
  const token = issued?.secret ?? null;
  if (!client) return null;

  return (
    <section className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-col gap-0.5">
        <h2 className="text-sm font-medium">{t("integrations.connect.title")}</h2>
        <p className="text-xs text-muted-foreground">
{t("integrations.connect.description")}
        </p>
      </div>

      {issued ? (
        <div className="flex flex-col gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5">
          <p className="text-xs">
{t("integrations.connect.ready", { name: issued.name })}
          </p>
          <CopyableCode label={t("integrations.connect.tokenLabel")} value={issued.secret} />
          <ConnectionCheck token={issued.secret} url={url} />
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {mcpClients.map((candidate) => (
          <button
            className={cn(
              "rounded-md px-2.5 py-1 text-xs transition-colors",
              candidate.id === client.id ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:bg-accent/50",
            )}
            key={candidate.id}
            onClick={() => setClientId(candidate.id)}
            type="button"
          >
            {candidate.name}
          </button>
        ))}
      </div>

      <div className="flex min-w-0 flex-col gap-2.5">
        <p className="text-xs text-muted-foreground">{t(`mcpClients.${client.id}.description`)}</p>
        <ol className="flex flex-col gap-1 text-xs text-muted-foreground">
          {Array.from({ length: client.stepCount }, (_, index) => (
            <li className="flex gap-2" key={index}>
              <span className="w-3 shrink-0 tabular-nums text-muted-foreground/70">{index + 1}</span>
              <span className="min-w-0">{t(`mcpClients.${client.id}.step${index + 1}`)}</span>
            </li>
          ))}
        </ol>
        <CopyableCode label={client.configLabel({ token })} value={client.snippet({ token, url })} />
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <h3 className="text-xs font-medium text-muted-foreground">{t("integrations.connect.tryWith")}</h3>
        {starterPromptKeys.map((key) => (
          <CopyableCode key={key} value={t(`mcpPrompts.${key}`)} />
        ))}
      </div>
    </section>
  );
}

// Answering "is it actually working" here, against the same endpoint and the
// same token, means nobody has to debug a config file to find out that the
// token was the problem.
function ConnectionCheck({ token, url }: { token: string; url: string }) {
  const t = useT();
  const { locale } = useI18n();
  const [state, setState] = useState<{ kind: "error"; message: string } | { kind: "idle" } | { kind: "ok"; tools: number } | { kind: "testing" }>({
    kind: "idle",
  });

  async function test() {
    setState({ kind: "testing" });
    try {
      const response = await fetch(url, {
        body: JSON.stringify({ id: 1, jsonrpc: "2.0", method: "tools/list" }),
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        method: "POST",
      });
      const payload = (await response.json()) as { result?: { tools?: unknown[] } };
      const tools = payload.result?.tools?.length ?? 0;
      setState(tools > 0 ? { kind: "ok", tools } : { kind: "error", message: t("integrations.connect.testEmpty") });
    } catch (error) {
      setState({ kind: "error", message: error instanceof Error ? error.message : t("integrations.connect.testFailed") });
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button className="h-7 text-xs" disabled={state.kind === "testing"} onClick={() => void test()} size="sm" variant="outline">
        {state.kind === "testing" ? <Loader2 className="animate-spin" data-icon="inline-start" /> : <Plug data-icon="inline-start" />}
        {t("integrations.connect.testConnection")}
      </Button>
      {state.kind === "ok" ? (
        <span className="text-xs text-muted-foreground">{t("integrations.connect.testConnected", { count: state.tools })}</span>
      ) : null}
      {state.kind === "error" ? <span className="text-xs text-destructive">{state.message}</span> : null}
    </div>
  );
}

function CopyableCode({ label, value }: { label?: string; value: string }) {
  const t = useT();
  const { locale } = useI18n();
  const [isCopied, setIsCopied] = useState(false);
  return (
    <div className="flex min-w-0 flex-col gap-1">
      {label ? <span className="text-xs text-muted-foreground">{label}</span> : null}
      <div className="flex items-center gap-2 overflow-hidden rounded-md border border-border/60 bg-muted/40 px-3 py-2">
        {/* `block`, or the overflow rule is ignored on an inline element and a
            long connect command pushes the copy button out of the dialog. */}
        <code className="block min-w-0 flex-1 overflow-x-auto whitespace-pre text-xs leading-relaxed">{value}</code>
        <Button
          className="h-7 shrink-0 text-xs"
          onClick={() => {
            void navigator.clipboard.writeText(value);
            setIsCopied(true);
            window.setTimeout(() => setIsCopied(false), 1500);
          }}
          size="sm"
          variant="ghost"
        >
          {isCopied ? <Check data-icon="inline-start" /> : <Copy data-icon="inline-start" />}
          {isCopied ? t("common.copied") : t("common.copy")}
        </Button>
      </div>
    </div>
  );
}

function CreateTokenDialog({
  onCreated,
  onOpenChange,
  open,
}: {
  onCreated: (created: { name: string; secret: string }) => Promise<void> | void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
}) {
  const t = useT();
  const { locale } = useI18n();
  const trpc = useTRPC();
  const createMutation = useMutation(trpc.apiTokens.create.mutationOptions());
  // Service accounts are the other thing a token can act as. The query is only
  // read here — creating one belongs on the Team screen, where access is given.
  const teamQuery = useQuery(trpc.access.team.queryOptions());
  const serviceAccounts = (teamQuery.data?.members ?? []).filter((member) => member.isServiceAccount);
  const [name, setName] = useState("");
  const [ownerUserId, setOwnerUserId] = useState<string | null>(null);
  // По умолчанию токен только читает: право на запись включают осознанно.
  const [scopes, setScopes] = useState<string[]>([]);
  const [expiresInDays, setExpiresInDays] = useState<number>(defaultExpiryDays);
  const [error, setError] = useState<string | null>(null);

  function close() {
    onOpenChange(false);
    setName("");
    setOwnerUserId(null);
    setScopes([]);
    setExpiresInDays(defaultExpiryDays);
    setError(null);
  }

  async function create() {
    setError(null);
    try {
      const created = await createMutation.mutateAsync({ expiresInDays, name: name.trim(), ownerUserId, scopes: ["read", ...scopes] });
      await onCreated({ name: created.name, secret: created.secret });
      setName("");
      setScopes([]);
      setExpiresInDays(defaultExpiryDays);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : t("integrations.tokens.create.failed"));
    }
  }

  return (
    <Dialog onOpenChange={(next) => (next ? onOpenChange(true) : close())} open={open}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("integrations.tokens.create.title")}</DialogTitle>
          <DialogDescription>{t("integrations.tokens.create.description")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="token-name">{t("integrations.tokens.create.name")}</Label>
            <Input
              autoFocus
              id="token-name"
              onChange={(event) => setName(event.target.value)}
              placeholder="Claude Code"
              value={name}
            />
          </div>
          {serviceAccounts.length > 0 ? (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium">{t("integrations.tokens.create.actsAs")}</span>
              <div className="flex flex-col gap-1">
                <label className="flex items-start gap-2.5">
                  <input
                    checked={ownerUserId === null}
                    className="mt-1"
                    name="token-owner"
                    onChange={() => setOwnerUserId(null)}
                    type="radio"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm">{t("integrations.tokens.create.actsAsYou")}</span>
                    <span className="text-xs text-muted-foreground">{t("integrations.tokens.create.actsAsYouHint")}</span>
                  </span>
                </label>
                {serviceAccounts.map((account) => (
                  <label className="flex items-start gap-2.5" key={account.userId}>
                    <input
                      checked={ownerUserId === account.userId}
                      className="mt-1"
                      name="token-owner"
                      onChange={() => setOwnerUserId(account.userId)}
                      type="radio"
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm">{account.displayName ?? account.email}</span>
                      <span className="text-xs text-muted-foreground">{t("integrations.tokens.create.actsAsServiceHint")}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">{t("integrations.tokens.create.canDo")}</span>
            <p className="text-xs text-muted-foreground">{t("integrations.tokens.create.canDoHint")}</p>
            {optionalScopes.map((option) => (
              <label className="flex items-start gap-2.5" key={option.scope}>
                <Checkbox
                  checked={scopes.includes(option.scope)}
                  className="mt-0.5"
                  onCheckedChange={(checked) =>
                    setScopes((current) =>
                      checked ? [...new Set([...current, option.scope])] : current.filter((scope) => scope !== option.scope),
                    )
                  }
                />
                <span className="flex min-w-0 flex-col">
                  <span className="text-sm">{t(`integrations.tokens.create.${option.key}Label`)}</span>
                  <span className="text-xs text-muted-foreground">{t(`integrations.tokens.create.${option.key}Description`)}</span>
                </span>
              </label>
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="token-expiry">{t("integrations.tokens.create.expiry")}</Label>
            <select
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
              id="token-expiry"
              onChange={(event) => setExpiresInDays(Number(event.target.value))}
              value={expiresInDays}
            >
              {expiryOptions.map((days) => (
                <option key={days} value={days}>
                  {days === 365 ? t("integrations.tokens.create.expiryYear") : t("integrations.tokens.create.expiryDays", { count: days })}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">{t("integrations.tokens.create.expiryHint")}</p>
          </div>
          {error ? <p className="text-xs text-destructive">{error}</p> : null}
        </div>
        <DialogFooter>
          <Button onClick={close} type="button" variant="ghost">
            {t("common.cancel")}
          </Button>
          <Button disabled={name.trim().length === 0 || createMutation.isPending} onClick={() => void create()}>
            {createMutation.isPending ? <Loader2 className="animate-spin" data-icon="inline-start" /> : null}
            {t("integrations.tokens.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
